import { __decorate } from "tslib";
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { DAY_NAMES } from '@/constants/DayNames';
import Vue from 'vue';
//#region ENUMS
import { InstructorTableRowType } from '@/enums/InstructorTableRowType';
import { InstructorTableCellType } from '@/enums/InstructorTableCellType';
//#endregion
//#region CLASSES
import InstructorTable from '@/classes/InstructorTable';
import OperatingHoursMapper from '@/classes/OperatingHoursMapper';
import InstructorsTableBase from '@/classes/InstructorsTableBase';
import InstructorTableAvailableCell from '@/classes/InstructorTableAvailableCell';
import InstructorTableBlockRow from '@/classes/InstructorTableBlockRow';
import InstructorTableInstructorRow from '@/classes/InstructorTableInstructorRow';
//#endregion
//#region COMPONENTS
import InstructorsTableReservedCell from '@/components/InstructorsTableReservedCell.vue';
import InstructorsTableAvailableCell from '@/components/InstructorsTableAvailableCell.vue';
import InstructorsTablePauseCell from '@/components/InstructorsTablePauseCell.vue';
import InstructorsTableEmptyCell from '@/components/InstructorsTableEmptyCell.vue';
import InstructorsTableKidSchoolCell from '@/components/InstructorsTableKidSchoolCell.vue';
import Snackbar from "@/components/Snackbar.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import InstructorsTableInstructorKidSchoolCell from "@/components/InstructorsTableInstructorKidSchoolCell.vue";
import AddKidSchoolForm from "@/components/AddKidSchoolForm.vue";
import InstructorSkillIcons from "@/components/InstructorSkillIcons.vue";
import _ from 'lodash';
import { isDesktop } from "@/utils/isDesktop";
//#endregion
let InstructorsTable = class InstructorsTable extends InstructorsTableBase {
    date;
    minHours;
    maxHours;
    showOtherInstructors;
    showScrollBar;
    activeFilters;
    listeners = [];
    concludeDialog = false;
    concluding = false;
    now = moment();
    isBlockRow(row) {
        return row.rowType === InstructorTableRowType.BLOCK;
    }
    isPauseCell(hour, row) {
        let cell = this.findCellInRow(hour, row);
        if (cell === undefined) {
            return false;
        }
        return cell.cellType === InstructorTableCellType.PAUSE;
    }
    isReservedCell(hour, row) {
        let cell = this.findCellInRow(hour, row);
        if (cell === undefined) {
            return false;
        }
        return cell.cellType === InstructorTableCellType.RESERVED;
    }
    isAvailableCell(hour, row) {
        let cell = this.findCellInRow(hour, row);
        if (cell === undefined) {
            return false;
        }
        return cell.cellType === InstructorTableCellType.AVAILABLE;
    }
    isKidSchoolCell(hour, row) {
        let cell = this.findCellInRow(hour, row);
        if (cell === undefined) {
            return false;
        }
        return cell.cellType === InstructorTableCellType.KID_SCHOOL;
    }
    isEmptyCell(hour, row) {
        let cell = this.findCellInRow(hour, row);
        return cell === undefined;
    }
    isOutOfTable(hour) {
        return hour < this.dataSource.fromHour || hour > this.dataSource.toHour;
    }
    getCellColSpan(hour, row) {
        let cell = this.findCellInRow(hour, row);
        if (cell === undefined) {
            return 12;
        }
        return cell.length * 12;
    }
    getCurrentUserRow() {
        return this.dataSource.rows.find(x => x.rowType === InstructorTableRowType.INSTRUCTOR
            && x.isCurrentUserRow());
    }
    hasCurrentUserCells() {
        return this.getCurrentUserRow().cells.some(x => x.cellType === InstructorTableCellType.RESERVED);
    }
    getRowHourList(row) {
        const result = [];
        for (let i = this.dataSource.fromHour; i <= this.dataSource.toHour; i++) {
            const cell = row.cells.find(x => x.hour === i);
            result.push(i);
            if (cell !== undefined) {
                i += cell.length - 1;
            }
        }
        return result;
    }
    getSchoolHourList(kidSchoolRow) {
        const result = [];
        let last = undefined;
        for (let h = this.minHours; h <= this.maxHours; h++) {
            const cells = [];
            for (let m = 0; m < 60; m = m + 5) {
                const time = moment(h + ':' + m, 'hh:mm');
                const cell = kidSchoolRow.find(x => moment(x.hourFrom + ':' + x.minuteFrom, 'hh:mm') <= time && moment(x.hourTo + ':' + x.minuteTo, 'hh:mm') > time);
                if (cell === undefined) {
                    cells.push({ isSchool: false, data: undefined });
                }
                else {
                    cells.push({ isSchool: true, data: cell });
                }
            }
            for (let m = 0; m < 12; m++) {
                if (last === cells[m].isSchool) {
                    result[result.length - 1].length++;
                }
                else {
                    result.push({
                        hour: h,
                        minute: m,
                        type: cells[m].isSchool ? 's' : 'e',
                        length: 1,
                        data: cells[m].data ?? {}
                    });
                    last = cells[m].isSchool;
                }
            }
            if (!last) {
                last = undefined;
            }
        }
        return result;
    }
    getTableHourList(row) {
        const result = [];
        for (let i = this.minHours; i <= this.maxHours; i++) {
            const cell = row.cells.find(x => x.hour === i);
            result.push(i);
            if (cell !== undefined) {
                i += cell.length - 1;
            }
        }
        return result;
    }
    hasOnlyEmptyCells(row) {
        return !row.cells.some(cell => [
            InstructorTableCellType.AVAILABLE,
            InstructorTableCellType.RESERVED
        ].includes(cell.cellType));
    }
    getInstructorShortName(id) {
        const instructor = this.getInstructorById(id);
        const splitName = instructor.name.split(' ');
        const lastName = splitName.pop() ?? '';
        const firstName = splitName.join(' ');
        if (isDesktop()) {
            return instructor.name.length > 18
                ? firstName.substr(0, 1) + '. ' + lastName
                : instructor.name;
        }
        else {
            return firstName.substr(0, 1) + lastName.substr(0, 1);
        }
    }
    getInstructorAvatar(id) {
        return this.getInstructorById(id).avatar;
    }
    getInstructorFullName(id) {
        return this.getInstructorById(id).name;
    }
    getInstructorSkills(id) {
        const instructor = this.$store.getters['instructor/get'](id);
        return instructor?.data?.skills ?? [];
    }
    getInstructorById(id) {
        const instructor = this.$store.getters['instructor/fullNames'].find((x) => x.id === id);
        return instructor ?? { id: 0, name: '', avatar: '' };
    }
    isInstructorDeleted(instructorId) {
        // Use 'instructor/all' getter to establish proper Vue reactivity tracking.
        // The 'instructor/get' getter returns a function which doesn't properly
        // trigger re-renders when instructor data updates.
        const instructors = this.$store.getters['instructor/all'];
        const instructor = instructors.find((i) => i.id === instructorId);
        return !!instructor?.data?.deletedAt;
    }
    getInstructorAvatarById(id) {
        return (this.$store.getters['instructor/avatars'].find((x) => x.id === id)?.name ?? '');
    }
    hasAnyReservedCells(row) {
        return row.cells.some(x => x.cellType === InstructorTableCellType.RESERVED);
    }
    isRowEmpty(row) {
        return !row.cells.some(x => x.cellType !== InstructorTableCellType.PAUSE);
    }
    async addBlockToCurrentUser(row) {
        this.dataSource.rows.splice(this.dataSource.rows.indexOf(row), 1);
        const currentUserRow = this.getCurrentUserRow();
        currentUserRow.addedDate = moment();
        currentUserRow.cells = row.cells;
        await this.updateDataSource();
    }
    async addAvailableCellsToCurrentUser() {
        const currentUserRow = this.getCurrentUserRow();
        this.getRowHourList(currentUserRow).forEach(i => {
            if (!currentUserRow.cells.some(x => x.hour == i)) {
                const availableCell = new InstructorTableAvailableCell(currentUserRow);
                availableCell.hour = i;
                currentUserRow.cells.push(availableCell);
            }
        });
        await this.updateDataSource();
    }
    async removeAvailableCellsToCurrentUser() {
        const currentUserRow = this.getCurrentUserRow();
        if (this.hasAnyReservedCells(currentUserRow)) {
            if (!await ConfirmDialog.open('Upozornění', 'Naplánované lekce nelze odebrat. Odstraněny budou pouze volné hodiny. Pokud nemůžeš tento den vyučovat, neprodleně kontaktuj službu!', 'Rozumím', 'Zrušit')) {
                return;
            }
        }
        this.getRowHourList(currentUserRow).forEach(i => {
            if (currentUserRow.cells.some(x => x.hour == i && x.cellType === InstructorTableCellType.AVAILABLE)) {
                currentUserRow.cells.splice(currentUserRow.cells.findIndex(x => x.hour == i && x.cellType === InstructorTableCellType.AVAILABLE), 1);
            }
        });
        await this.updateDataSource();
    }
    async removeAvailableCellToCurrentUser(hour, row) {
        const currentUserRow = this.getCurrentUserRow();
        if (row === currentUserRow) {
            const index = currentUserRow.cells.findIndex(x => x.hour === hour);
            if (index !== -1) {
                currentUserRow.cells.splice(index, 1);
            }
        }
        await this.updateDataSource();
    }
    mounted() {
        setTimeout(async () => {
            const date = _.cloneDeep(this.date);
            await this.$store.dispatch('instructorTable/loadDates', [date]);
            this.onTableWidthChange(this.tableWidth);
            setTimeout(async () => {
                this.listeners.push(await this.$store.dispatch('instructorTable/listen', { date }));
            }, 0);
        }, 0);
    }
    beforeDestroy() {
        for (let endListener of this.listeners) {
            endListener();
        }
    }
    async addBlockRow() {
        const blockRow = new InstructorTableBlockRow(this.dataSource);
        if (this.dataSource.rows.some(x => x.rowType === InstructorTableRowType.BLOCK)) {
            blockRow.blockId = Math.min(...this.dataSource.rows.filter(x => x.rowType === InstructorTableRowType.BLOCK)
                .map(x => x.blockId)) - 1;
        }
        else {
            blockRow.blockId = -1;
        }
        this.dataSource.rows.push(blockRow);
        await this.updateDataSource();
    }
    async removeBlockRow(row) {
        this.dataSource.rows.splice(this.dataSource.rows.indexOf(row), 1);
        await this.updateDataSource();
    }
    async conclude() {
        this.concluding = true;
        try {
            await this.dataSource.conclude();
            this.concludeDialog = false;
            this.concluding = false;
            Snackbar.info('Den uzavřen.');
        }
        catch (e) {
            this.concludeDialog = false;
            this.concluding = false;
            Snackbar.error('Den se nepodařilo uzavřít!');
            console.error('Den se nepodařilo uzavřít!', e);
        }
    }
    async editKidSchool(school) {
        await this.$refs['kidSchoolForm'].open(school);
    }
    async addKidSchool() {
        await this.$refs['kidSchoolForm'].open();
    }
    hasInstructorRowSomeCellExceptSchool(row) {
        if (row.rowType !== InstructorTableRowType.INSTRUCTOR
            || row.isCurrentUserRow()) {
            return true;
        }
        return row.cells.some(x => x.cellType !== InstructorTableCellType.KID_SCHOOL
            && x.cellType !== InstructorTableCellType.PAUSE);
    }
    async submitKidSchoolForm() {
        await this.updateDataSource();
    }
    onHoursChange(hours) {
        this.$store.dispatch('instructorTable/refreshDate', {
            date: this.date,
            hours: hours,
        });
    }
    onTableWidthChange(width) {
        const innerTable = document.querySelector('.table-wrapper-' + this.$vnode.key + ' .v-data-table__wrapper table');
        if (innerTable === null) {
            return;
        }
        innerTable.style.width = width + 'px';
        document.dispatchEvent(new Event('instructorTableWidthChanged'));
    }
    get dataSourceRows() {
        return this.dataSource.getOrderedRows(this.showOtherInstructors).filter((row) => {
            if (this.activeFilters.length === 0) {
                return true;
            }
            if (row instanceof InstructorTableInstructorRow) {
                const instructor = this.$store.getters['instructor/get'](row.instructorId);
                for (const skill of instructor.data.skills ?? []) {
                    if (this.activeFilters.includes(skill)) {
                        return true;
                    }
                }
                return false;
            }
            return true;
        });
    }
    get hasEmptyRowWithData() {
        return this.dataSource.rows.some(row => row.rowType === InstructorTableRowType.BLOCK
            && row.cells.some(cell => cell.cellType === InstructorTableCellType.RESERVED));
    }
    get hours() {
        return OperatingHoursMapper.getByDay(this.date);
    }
    get dataSource() {
        let result = this.$store.getters['instructorTable/get'](this.date);
        if (result === undefined) {
            result = new InstructorTable();
            result.date = this.date;
            result.fromHours = 1;
            result.toHours = 2;
        }
        return result;
    }
    get tableName() {
        return isDesktop()
            ? `${DAY_NAMES[this.dataSource.date.day()]} (${this.dataSource.date.toDate().toLocaleDateString()})`
            : `${DAY_NAMES[this.dataSource.date.day()].substr(0, 2)} - ${this.dataSource.date.format('DD.MM.')}`;
    }
    get isDesktop() {
        return isDesktop();
    }
    get instructorColumnWidth() {
        return isDesktop() ? 250 : 95;
    }
    get cellColumnWidth() {
        return isDesktop() ? 15 : 7;
    }
    get tableWidth() {
        return (this.instructorColumnWidth +
            (this.maxHours - this.minHours + 1) * this.cellColumnWidth * 12) + 2;
    }
    get tableClasses() {
        const classes = {
            'without-scrollbar': !this.showScrollBar,
        };
        Vue.set(classes, `table-wrapper-${this.$vnode.key}`, true);
        return classes;
    }
    get hasDaySchool() {
        return this.dataSource.kidSchools.length !== 0;
    }
    get kidSchoolRows() {
        const result = [];
        let numberOfRows = -1;
        for (const kidSchool of this.dataSource.kidSchools) {
            let isSchoolAdded = false;
            for (const row of result) {
                let isPossibleAddKidSchoolToRow = true;
                const rowTimes = row.map(x => {
                    return [
                        `${x.hourFrom < 10 ? '0' + x.hourFrom : x.hourFrom}:${x.minuteFrom === 0 ? '00' : x.minuteFrom}`,
                        `${x.hourTo < 10 ? '0' + x.hourTo : x.hourTo}:${x.minuteTo === 0 ? '00' : x.minuteTo}`,
                    ];
                });
                rowTimes.push([
                    `${kidSchool.hourFrom < 10 ? '0' + kidSchool.hourFrom : kidSchool.hourFrom}:${kidSchool.minuteFrom === 0 ? '00' : kidSchool.minuteFrom}`,
                    `${kidSchool.hourTo < 10 ? '0' + kidSchool.hourTo : kidSchool.hourTo}:${kidSchool.minuteTo === 0 ? '00' : kidSchool.minuteTo}`,
                ]);
                rowTimes.sort((time1, time2) => time1[0].localeCompare(time2[0]));
                for (let i = 0; i < rowTimes.length - 1; i++) {
                    const currentEndTime = rowTimes[i][1];
                    const nextStartTime = rowTimes[i + 1][0];
                    if (currentEndTime > nextStartTime) {
                        isPossibleAddKidSchoolToRow = false;
                        break;
                    }
                }
                if (isPossibleAddKidSchoolToRow) {
                    row.push(kidSchool);
                    isSchoolAdded = true;
                    break;
                }
            }
            if (!isSchoolAdded) {
                result.push([]);
                numberOfRows++;
                result[numberOfRows].push(kidSchool);
            }
        }
        return result;
    }
};
__decorate([
    Prop({ required: true })
], InstructorsTable.prototype, "date", void 0);
__decorate([
    Prop({ required: true })
], InstructorsTable.prototype, "minHours", void 0);
__decorate([
    Prop({ required: true })
], InstructorsTable.prototype, "maxHours", void 0);
__decorate([
    Prop({ required: true })
], InstructorsTable.prototype, "showOtherInstructors", void 0);
__decorate([
    Prop({ default: false })
], InstructorsTable.prototype, "showScrollBar", void 0);
__decorate([
    Prop({ default: [] })
], InstructorsTable.prototype, "activeFilters", void 0);
__decorate([
    Watch('hours', {
        deep: true,
    })
], InstructorsTable.prototype, "onHoursChange", null);
__decorate([
    Watch('tableWidth')
], InstructorsTable.prototype, "onTableWidthChange", null);
InstructorsTable = __decorate([
    Component({
        components: {
            AddKidSchoolForm,
            InstructorsTableReservedCell,
            InstructorsTableAvailableCell,
            InstructorsTablePauseCell,
            InstructorsTableEmptyCell,
            InstructorsTableKidSchoolCell,
            InstructorsTableInstructorKidSchoolCell,
            InstructorSkillIcons,
        },
    })
], InstructorsTable);
export default InstructorsTable;
